import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
type EntityPermissionState = {
  members: EntityMember[];
  invitations: EntityInvitation[];
  catalog: EntityPermissionCatalogItem[];
  assignmentsByMember: Record<string, EntityResourceAssignment[]>;
  assignableResourcesByMember: Record<string, Partial<Record<EntityAssignableResourceType, EntityAssignableResourcePage>>>;
};

export const entityPermissionInitialState: EntityPermissionState = {
  members: [],
  invitations: [],
  catalog: [],
  assignmentsByMember: {},
  assignableResourcesByMember: {},
};

const slice = createSlice({
  name: 'entityPermission',
  initialState: entityPermissionInitialState,
  reducers: {
    setEntityPermissionMembers(state, action: PayloadAction<EntityMember[]>) {
      state.members = action.payload;
    },
    upsertEntityPermissionMember(state, action: PayloadAction<EntityMember>) {
      const index = state.members.findIndex((member) => (
        member.id === action.payload.id
        || member.membershipId === action.payload.membershipId
      ));
      if (index >= 0) state.members[index] = action.payload;
      else state.members.push(action.payload);
    },
    setEntityPermissionInvitations(state, action: PayloadAction<EntityInvitation[]>) {
      state.invitations = action.payload;
    },
    setEntityPermissionCatalog(state, action: PayloadAction<EntityPermissionCatalogItem[]>) {
      state.catalog = action.payload;
    },
    setMemberResourceAssignments(
      state,
      action: PayloadAction<{ membershipId: string; assignments: EntityResourceAssignment[] }>,
    ) {
      state.assignmentsByMember[action.payload.membershipId] = action.payload.assignments;
    },
    setAssignableResources(
      state,
      action: PayloadAction<{
        membershipId: string;
        resourceType: EntityAssignableResourceType;
        page: EntityAssignableResourcePage;
        append?: boolean;
      }>,
    ) {
      const { membershipId, resourceType, page, append } = action.payload;
      const current = state.assignableResourcesByMember[membershipId]?.[resourceType];
      const items = append && current
        ? [...current.items, ...page.items.filter((item) => !current.items.some((currentItem) => currentItem.resourceId === item.resourceId))]
        : page.items;

      state.assignableResourcesByMember[membershipId] = {
        ...state.assignableResourcesByMember[membershipId],
        [resourceType]: { ...page, items },
      };
    },
  },
});

export const {
  setEntityPermissionMembers,
  upsertEntityPermissionMember,
  setEntityPermissionInvitations,
  setEntityPermissionCatalog,
  setMemberResourceAssignments,
  setAssignableResources,
} = slice.actions;

export default slice.reducer;
